import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { newsData, categoryColors } from '../../data/newsData';
import type { NewsItem } from '../../data/newsData';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<NewsItem | null>(null);
  const [prevArticle, setPrevArticle] = useState<NewsItem | null>(null);
  const [nextArticle, setNextArticle] = useState<NewsItem | null>(null);
  const [loaded, setLoaded] = useState(false);

  const handleBackToNews = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('news');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const currentId = Number(id);
    const currentArticle = newsData.find((item) => item.id === currentId);

    if (currentArticle) {
      setArticle(currentArticle);

      const currentIndex = newsData.findIndex((item) => item.id === currentId);
      setPrevArticle(currentIndex > 0 ? newsData[currentIndex - 1] : null);
      setNextArticle(currentIndex < newsData.length - 1 ? newsData[currentIndex + 1] : null);
    }

    const timer = setTimeout(() => setLoaded(true), 50);
    return () => clearTimeout(timer);
  }, [id]);

  useEffect(() => {
    if (!article) return;
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const pageUrl = `${siteUrl}/news/${article.id}`;
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'NewsArticle',
          '@id': `${pageUrl}/#article`,
          headline: article.title,
          description: article.summary,
          url: pageUrl,
          image: article.image,
          datePublished: article.date,
          dateModified: article.date,
          author: {
            '@type': 'Organization',
            name: '株式会社永恒',
            url: siteUrl,
          },
          publisher: {
            '@type': 'Organization',
            name: '株式会社永恒',
            url: siteUrl,
            logo: {
              '@type': 'ImageObject',
              url: `${siteUrl}/vite.svg`,
            },
          },
          articleSection: article.category,
          inLanguage: 'ja',
        },
        {
          '@type': 'WebPage',
          '@id': `${pageUrl}/#webpage`,
          url: pageUrl,
          name: `${article.title} | 株式会社永恒`,
          description: article.summary,
          inLanguage: 'ja',
          isPartOf: { '@id': `${siteUrl}/#website` },
        },
      ],
    };

    const existingScript = document.getElementById('schema-news');
    if (existingScript) existingScript.remove();
    const script = document.createElement('script');
    script.id = 'schema-news';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    document.title = `${article.title} | 株式会社永恒`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', article.summary.slice(0, 160));
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', pageUrl);

    return () => {
      const s = document.getElementById('schema-news');
      if (s) s.remove();
    };
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">記事が見つかりません</h2>
          <button
            onClick={handleBackToNews}
            className="inline-flex items-center gap-2 text-[#B8960C] hover:underline cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            ニュース一覧に戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section
        className="relative w-full h-[500px] overflow-hidden"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'scale(1)' : 'scale(0.97)',
          transition: 'opacity 0.8s ease, transform 0.8s ease',
        }}
      >
        <img
          src={article.image}
          alt={article.title}
          title={`${article.title} | 株式会社永恒`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>

        <div className="absolute inset-0 flex items-end">
          <div className="max-w-5xl mx-auto px-8 pb-16 w-full">
            <div className="flex items-center gap-3 mb-4">
              <span
                className={`text-xs px-3 py-1.5 font-medium rounded-sm ${
                  categoryColors[article.category] || 'bg-gray-100 text-gray-600'
                }`}
              >
                {article.category}
              </span>
              <span className="text-white/90 text-sm">{article.date}</span>
              {article.tag === 'hot' && (
                <div className="bg-red-500 text-white text-xs px-2.5 py-1 font-medium tracking-wider">
                  NEW
                </div>
              )}
              {article.tag === 'award' && (
                <div className="bg-[#B8960C] text-white text-xs px-2.5 py-1 font-medium tracking-wider flex items-center gap-1">
                  <i className="ri-award-line text-xs"></i> 受賞
                </div>
              )}
            </div>
            <h1
              className="text-4xl lg:text-5xl font-bold text-white leading-tight"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              {article.title}
            </h1>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section
        className="py-20 bg-[#F9F7F4]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
        }}
      >
        <div className="max-w-4xl mx-auto px-8">
          <div className="mb-12">
            <button
              onClick={handleBackToNews}
              className="inline-flex items-center gap-2 text-[#B8960C] hover:text-[#8B6F0A] transition-colors cursor-pointer text-sm font-medium"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              ニュース一覧に戻る
            </button>
          </div>

          <article className="bg-white rounded-sm shadow-sm p-12">
            <div className="prose prose-lg max-w-none">
              <div className="text-gray-700 leading-relaxed whitespace-pre-line text-base">
                {article.content}
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-600">この記事をシェア：</span>
                <div className="flex items-center gap-3">
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-[#B8960C] hover:text-white transition-all cursor-pointer"
                    aria-label="Twitterでシェア"
                  >
                    <i className="ri-twitter-x-line text-lg"></i>
                  </button>
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-[#B8960C] hover:text-white transition-all cursor-pointer"
                    aria-label="Facebookでシェア"
                  >
                    <i className="ri-facebook-fill text-lg"></i>
                  </button>
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-[#B8960C] hover:text-white transition-all cursor-pointer"
                    aria-label="LINEでシェア"
                  >
                    <i className="ri-line-fill text-lg"></i>
                  </button>
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-[#B8960C] hover:text-white transition-all cursor-pointer"
                    aria-label="リンクをコピー"
                  >
                    <i className="ri-link text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </article>

          <div
            className="mt-12 grid md:grid-cols-2 gap-6"
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.8s ease 0.4s, transform 0.8s ease 0.4s',
            }}
          >
            {prevArticle ? (
              <Link
                to={`/news/${prevArticle.id}`}
                className="group bg-white rounded-sm shadow-sm hover:shadow-lg transition-all p-6 cursor-pointer"
              >
                <div className="flex items-center gap-2 text-[#B8960C] text-sm font-medium mb-3">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </div>
                  前の記事
                </div>
                <h3
                  className="text-base font-bold text-[#1A1A1A] group-hover:text-[#B8960C] transition-colors line-clamp-2"
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  {prevArticle.title}
                </h3>
              </Link>
            ) : (
              <div></div>
            )}

            {nextArticle ? (
              <Link
                to={`/news/${nextArticle.id}`}
                className="group bg-white rounded-sm shadow-sm hover:shadow-lg transition-all p-6 cursor-pointer"
              >
                <div className="flex items-center justify-end gap-2 text-[#B8960C] text-sm font-medium mb-3">
                  次の記事
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
                <h3
                  className="text-base font-bold text-[#1A1A1A] group-hover:text-[#B8960C] transition-colors line-clamp-2 text-right"
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  {nextArticle.title}
                </h3>
              </Link>
            ) : (
              <div></div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
